import React, { useState, useEffect, useContext } from "react";
import { Tab, TabList, Field, Combobox, Option } from "@fluentui/react-components";
import {
  bundleIcon,
  DatabaseRegular,
  DatabaseFilled,
  DataHistogramFilled,
  DataHistogramRegular,
} from "@fluentui/react-icons";
import type { SelectTabData, SelectTabEvent, TabValue } from "@fluentui/react-components";
import { ProfileService } from "../../service/ProfileService";
import MasterData from "./MasterData";
import UniverseAnalytic from "./UniverseAnalytic";
import Header from "./Header";
import Loader from "./Loader";
import { AppContext } from "./context/AppContext";
import { ProfileSettings } from "../../types/app.storage.enums";
import {
  saveCustomFieldMasterData,
  saveLicenseDetails,
  savePmeIndex,
  saveMeasurementDates,
  saveCurrency,
  saveAlternateBaseCurrency,
  saveCurrencyHedge,
  isLicenseFlagEnabled,
} from "../../utils/profilepreferencesave";
import { setStringToStorage, getStringFromStorage, clearAllLocalStorage } from "../../utils/storagehelper";
import { useTabMenuStyles } from "./style/tabmenu.style";
import { useNavigate } from "react-router-dom";
import { isValidUser } from "../../utils/common";

const TabAnalyticMenuIcon = bundleIcon(DataHistogramFilled, DataHistogramRegular);
const TabDataMenuIcon = bundleIcon(DatabaseFilled, DatabaseRegular);
const DIALOG_FLAG_KEY = "xl_IsOfficeDialog";

const isOfficeDialogWindow = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("Popup") === "true") {
      return true;
    }
    if (params.get("Popup") === "false") {
      return false;
    }
    return sessionStorage.getItem(DIALOG_FLAG_KEY) === "true";
  } catch {
    return false;
  }
};

const TabMenu = () => {
  const styles = useTabMenuStyles();
  const [selectedValue, setSelectedValue] = React.useState<TabValue>("data");
  const [profilelist, setProfileList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProfileId, setSelectedProfileId] = useState([]);
  const [selectedProfileName, setSelectedProfileName] = useState("");
  const [hasLicense, setHasLicense] = useState(isLicenseFlagEnabled(ProfileSettings.LicenseFlag));
  const [hasUniversalAnalytics, setHasUniversalAnalytics] = useState(
    isLicenseFlagEnabled(ProfileSettings.UniversalAnalyticsFlag)
  );
  const [hasBasicAccess, setHasBasicAccess] = useState(
    isLicenseFlagEnabled(ProfileSettings.IsBasicAccessToAssetAndOrganizationsLicensed)
  );
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const handleIsValidUser = () => {
    if (isValidUser()) {
      navigate("/home");
      return;
    }
  };
  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    handleIsValidUser();
    setSelectedValue(data.value);
  };

  const clearLocalHistoryData = () => {
    clearAllLocalStorage();
    console.log("Local storage cleared successfully");
  };

  const setFlag = () => {
    const license = isLicenseFlagEnabled(ProfileSettings.LicenseFlag);
    const universalAnalytics = isLicenseFlagEnabled(ProfileSettings.UniversalAnalyticsFlag);
    const basicAccess = isLicenseFlagEnabled(ProfileSettings.IsBasicAccessToAssetAndOrganizationsLicensed);
    setHasLicense(license);
    setHasUniversalAnalytics(universalAnalytics);
    setHasBasicAccess(basicAccess);
    if (basicAccess || license) {
      setSelectedValue("data");
    } else if (universalAnalytics) {
      setSelectedValue("analytics");
    }
  };

  const notifyParentIfDialog = () => {
    if (!isOfficeDialogWindow()) {
      return;
    }
    try {
      const localStorageData = {};
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key) {
          const value = window.localStorage.getItem(key);
          if (value) {
            localStorageData[key] = value;
          }
        }
      }
      Office.context.ui.messageParent(
        JSON.stringify({
          type: "profileSelected",
          localStorage: localStorageData,
        })
      );
      clearLocalHistoryData();
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const saveProfilePreference = (profileid) => {
    if (!profileid) {
      return Promise.resolve();
    }
    setIsLoading(true);
    setStringToStorage(ProfileSettings.ProfileId, profileid);
    return saveLicenseDetails(profileid)
      .then(() => {
        setFlag();
        return Promise.all([
          saveCustomFieldMasterData(profileid),
          savePmeIndex(profileid),
          saveMeasurementDates(profileid),
          saveCurrency(profileid),
          saveAlternateBaseCurrency(profileid),
          saveCurrencyHedge(profileid),
        ]);
      })
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.log("Error: " + error);
        setFlag();
        setIsLoading(false);
      });
  };

  const profileChangeHandler = (_e, data) => {
    if (isValidUser()) {
      navigate("/home");
      return;
    }
    const profileId = data.optionValue || data.selectedOptions?.[0];
    const profile = profilelist.find((item) => item.profileID === profileId);
    if (!profile) {
      return;
    }
    setSelectedProfileId([profile.profileID]);
    setSelectedProfileName(profile.profileName || "");
    context.setSelectedProfile(profile);
    saveProfilePreference(profile.profileID);
  };

  const populateProfileList = () => {
    new ProfileService()
      .getProfiles()
      .then((profile) => {
        const profiles = Array.isArray(profile) ? profile : [];
        setProfileList(() => profiles);
        const profileID = getStringFromStorage(ProfileSettings.ProfileId);
        const selectedProfile = profiles.find((item) => item.profileID === profileID) || profiles[0];
        if (selectedProfile) {
          setSelectedProfileId(() => [selectedProfile.profileID]);
          setSelectedProfileName(() => selectedProfile.profileName || "");
          if (!context.selectedProfile) {
            context.setSelectedProfile(selectedProfile);
          }
          const alreadyLicensed =
            isLicenseFlagEnabled(ProfileSettings.LicenseFlag) ||
            isLicenseFlagEnabled(ProfileSettings.UniversalAnalyticsFlag) ||
            isLicenseFlagEnabled(ProfileSettings.IsBasicAccessToAssetAndOrganizationsLicensed);
          if (alreadyLicensed) {
            setFlag();
            setIsLoading(false);
            notifyParentIfDialog();
            return;
          }
          saveProfilePreference(selectedProfile.profileID).then(() => {
            notifyParentIfDialog();
          });
          return;
        }
        setIsLoading(false);
        notifyParentIfDialog();
        return;
      })
      .catch((error) => {
        console.log("Error: " + error);
        setProfileList([]);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isValidUser()) {
      navigate("/home");
      return;
    }
    populateProfileList();
  }, []);

  return (
    <>
      <Header></Header>
      <div className={styles.root}>
        <Field label="Profile" className={styles.input}>
          <Combobox
            spellCheck={false}
            value={selectedProfileName}
            selectedOptions={selectedProfileId}
            onOptionSelect={profileChangeHandler}
            onChange={(event) => setSelectedProfileName(event.target.value)}
            onBlur={() => {
              const selected = profilelist.find((item) => item.profileID === selectedProfileId[0]);
              if (selected) {
                setSelectedProfileName(selected.profileName || "");
              }
            }}
            listbox={{ className: styles.listbox }}
            placeholder="Select a profile"
          >
            {profilelist.map((item) => {
              return (
                <Option key={item.profileID} value={item.profileID}>
                  {item.profileName}
                </Option>
              );
            })}
          </Combobox>
        </Field>
        <TabList selectedValue={selectedValue} onTabSelect={onTabSelect} size="small">
          {(hasBasicAccess || hasLicense) && (
            <Tab id="data" icon={<TabDataMenuIcon />} value="data">
              Data
            </Tab>
          )}
          {hasUniversalAnalytics && (
            <Tab id="analytics" icon={<TabAnalyticMenuIcon />} value="analytics">
              Analytics
            </Tab>
          )}
        </TabList>
        <div className={styles.panels}>
          {selectedValue === "data" && (hasBasicAccess || hasLicense) && (
            <MasterData hasLicense={hasLicense} hasBasicAccess={hasBasicAccess} />
          )}
          {selectedValue === "analytics" && hasUniversalAnalytics && <UniverseAnalytic />}
          {!isLoading && !hasBasicAccess && !hasLicense && !hasUniversalAnalytics && (
            <div>No Data or Analytics access for this profile.</div>
          )}
        </div>
      </div>
      {isLoading && <Loader></Loader>}
    </>
  );
};

export default TabMenu;
